import { useState, useRef, useEffect } from 'react'
import { logout } from './sso.js'

const TIER_STYLES = {
  MAX:  { color: '#e8a020', bg: 'rgba(232,160,32,0.12)', border: 'rgba(232,160,32,0.35)' },
  PRO:  { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', border: 'rgba(139,92,246,0.35)' },
  FREE: { color: '#64748b', bg: 'rgba(100,116,139,0.12)', border: 'rgba(100,116,139,0.35)' },
}

const initials = (name) =>
  name
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('')

export default function UserBadge({ user }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const tier = TIER_STYLES[user.tier] || TIER_STYLES.FREE

  return (
    <div ref={ref} className="relative flex items-center gap-2">
      <span
        className="px-1.5 py-0.5 rounded font-mono text-2xs font-semibold tracking-widest uppercase"
        style={{ color: tier.color, background: tier.bg, border: `1px solid ${tier.border}` }}
      >
        {user.tier}
      </span>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${user.name}`}
        className="flex items-center gap-2 pl-1 pr-2 py-1 rounded hover:bg-surface2 transition-colors"
      >
        <span
          className="grid place-items-center w-6 h-6 rounded-full font-mono text-2xs font-semibold"
          style={{ background: 'rgba(232,160,32,0.15)', color: '#e8a020' }}
        >
          {initials(user.name)}
        </span>
        <span className="hidden sm:inline text-xs text-text-dim max-w-[14ch] truncate">
          {user.name}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-56 rounded border border-border bg-surface shadow-lg overflow-hidden"
          style={{ zIndex: 100 }}
        >
          <div className="px-3 py-2.5 border-b border-border">
            <div className="text-xs text-text truncate">{user.name}</div>
            <div className="text-2xs text-text-faint font-mono truncate">{user.email}</div>
          </div>
          <a
            href="https://identity.nexuslayer.eu"
            role="menuitem"
            className="block px-3 py-2 text-xs text-text-dim hover:text-text hover:bg-surface2 transition-colors"
          >
            NexusLayer Dashboard →
          </a>
          <button
            type="button"
            role="menuitem"
            onClick={logout}
            className="block w-full text-left px-3 py-2 text-xs text-text-dim hover:text-text hover:bg-surface2 transition-colors"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
